import { Alert, Button, FormField, Input, Spinner, Stack, Text } from '@ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { errorMessage, formErrors } from '../../../lib/errors';
import { useInvitationInfo, useRegisterWithInvitation, type InvitationInfo } from '../api';
import { PasswordInput } from '../components/PasswordInput';
import { PublicPage } from '../components/PublicPage';

/** Landing page of invitation links: the only way to create an account. */
export function InvitationPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const info = useInvitationInfo(token);

  if (info.isPending && token) {
    return (
      <PublicPage title={t('auth.invitation.title')}>
        <Spinner label={t('auth.invitation.loading')} />
      </PublicPage>
    );
  }
  if (!info.data) {
    return (
      <PublicPage title={t('auth.invitation.title')}>
        <Alert tone="danger">
          {info.error ? errorMessage(info.error) : t('errors.invitation.not_found')}
        </Alert>
        <Text size="sm">
          <Link to="/anmelden">{t('auth.forgot.backToLogin')}</Link>
        </Text>
      </PublicPage>
    );
  }

  return (
    <PublicPage
      title={t('auth.invitation.title')}
      description={
        <Stack gap={1}>
          <span>
            {info.data.inviter_name
              ? t('auth.invitation.intro', {
                  inviter: info.data.inviter_name,
                  business: info.data.business_name,
                })
              : t('auth.invitation.introSetup', { business: info.data.business_name })}
          </span>
          <span>{t('auth.invitation.role', { role: info.data.role_name })}</span>
        </Stack>
      }
    >
      <RegisterNew token={token} info={info.data} />
    </PublicPage>
  );
}

function RegisterNew({ token, info }: { token: string; info: InvitationInfo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const register = useRegisterWithInvitation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    register.mutate(
      { token, first_name: firstName, last_name: lastName, password },
      {
        // The first owner (invited during setup) continues with the business data.
        onSuccess: () => navigate(info.inviter_name ? '/' : '/willkommen', { replace: true }),
      },
    );
  };

  const errors = formErrors(register.error, {
    firstName: ['first_name.'],
    lastName: ['last_name.'],
    password: ['password.'],
  });
  return (
    <Stack as="form" gap={4} onSubmit={onSubmit} noValidate>
      {errors.general && <Alert tone="danger">{errors.general}</Alert>}
      <FormField label={t('auth.email')}>
        {(p) => <Input {...p} value={info.email} readOnly disabled />}
      </FormField>
      <FormField label={t('auth.firstName')} error={errors.field.firstName} required>
        {(p) => (
          <Input
            {...p}
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('auth.lastName')} error={errors.field.lastName} required>
        {(p) => (
          <Input
            {...p}
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        )}
      </FormField>
      <FormField
        label={t('auth.password')}
        hint={t('auth.passwordHint')}
        error={errors.field.password}
        required
      >
        {(p) => (
          <PasswordInput
            {...p}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </FormField>
      <Button type="submit" size="lg" fullWidth loading={register.isPending}>
        {t('auth.invitation.register')}
      </Button>
    </Stack>
  );
}
